import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Input, Select, Card, CardHeader, CardTitle, CardContent, ChevronLeft } from "@repo/o37-ui";
import VDRTrackingTable from '../components/VDRTrackingTable';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

export default function VDRSettings() {
  const { id: roomId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [room, setRoom] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Navigation State
  const [activeTab, setActiveTab] = useState<'general' | 'slugs' | 'tracking'>('general');

  // Settings State
  const [slug, setSlug] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [mainSecurityLevel, setMainSecurityLevel] = useState('OPEN');
  const [mainPassword, setMainPassword] = useState('');
  const [mainAllowedGroups, setMainAllowedGroups] = useState<string[]>([]);
  
  // Links State
  const [links, setLinks] = useState<any[]>([]);
  const [linkLabel, setLinkLabel] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  // Pages State
  const [pages, setPages] = useState<any[]>([]);
  const [isCreatingPage, setIsCreatingPage] = useState(false);

  // Domain Groups State (for email protection)
  const [domainGroups, setDomainGroups] = useState<any[]>([]);

  const userRole = localStorage.getItem('app_role') || 'user';
  const userId = localStorage.getItem('app_user_id') || '';

  useEffect(() => {
    if (roomId) {
      loadRoom();
      loadLinks();
      loadPages();
      loadDomainGroups();
    }
  }, [roomId, refreshKey]);

  const loadRoom = async () => {
    try {
      const res = await fetch(`${API_URL}/vdr/${roomId}`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      if (res.ok) {
        const data = await res.json();
        setRoom(data);
        setSlug(data.slug || '');
        setAccessCode(data.access_code || '');
        setLogoUrl(data.logo_url || '');
        setMainSecurityLevel(data.security_level || 'OPEN');
        setMainPassword(data.security_config?.password || '');
        setMainAllowedGroups(data.security_config?.allowed_domain_group_ids || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadLinks = async () => {
    try {
      const res = await fetch(`${API_URL}/vdr/${roomId}/links`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      if (res.ok) setLinks(await res.json());
    } catch (e) {
      console.error(e);
    }
  };

  const loadPages = async () => {
    try {
      const res = await fetch(`${API_URL}/vdr/${roomId}/pages`, {
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      if (res.ok) setPages(await res.json());
    } catch (e) {
      console.error("Failed to load pages", e);
    }
  };

  const loadDomainGroups = async () => {
    try {
      // Assuming endpoint exists based on DomainGroups page
      const res = await fetch(`${API_URL}/domain-groups`, {
        headers: { 'x-user-role': userRole }
      });
      if (res.ok) setDomainGroups(await res.json());
    } catch (e) {
      console.error("Failed to load domain groups", e);
    }
  };

  const handleSaveSettings = async () => {
    try {
      const securityConfig = {
          password: mainSecurityLevel === 'PASSWORD' ? mainPassword : null,
          allowed_domain_group_ids: mainSecurityLevel === 'EMAIL_OTP' ? mainAllowedGroups : []
      };

      const res = await fetch(`${API_URL}/vdr/${roomId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-user-role': userRole, 'x-user-id': userId },
        body: JSON.stringify({ 
            slug, 
            accessCode, // Legacy Master Password, kept for fallback or specific logic if needed, but new logic uses security_config
            logoUrl,
            securityLevel: mainSecurityLevel,
            securityConfig
        })
      });
      if (res.ok) {
        alert('Settings saved');
        setRefreshKey(k => k + 1);
      } else {
        alert('Failed to save settings');
      }
    } catch (e) { alert('Failed'); }
  };

  const handleCreateLink = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/vdr/${roomId}/links`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-role': userRole, 'x-user-id': userId },
        body: JSON.stringify({ label: linkLabel, code: customCode || undefined })
      });
      if (res.ok) {
        setLinkLabel('');
        setCustomCode('');
        loadLinks();
      } else {
        const err = await res.json();
        alert(err.error || 'Failed to create link');
      }
    } catch (e) {
      alert('Failed');
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    if (!confirm('Delete this link?')) return;
    try {
      await fetch(`${API_URL}/vdr/${roomId}/links/${linkId}`, {
        method: 'DELETE',
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      loadLinks();
    } catch (e) { alert('Failed'); }
  };

  const handleDeletePage = async (pageId: string) => {
    if (!confirm('Delete this page?')) return;
    try {
      await fetch(`${API_URL}/vdr/${roomId}/pages/${pageId}`, {
        method: 'DELETE',
        headers: { 'x-user-role': userRole, 'x-user-id': userId }
      });
      loadPages();
    } catch (e) { alert('Failed'); }
  };

  const baseUrl = import.meta.env.DEV ? "localhost:3000" : "vdr.o37.co";

  if (loading) return <div className="p-8">Loading settings...</div>;
  if (!room) return <div className="p-8">Room not found</div>;

  // Permissions Derived from API response (room.currentUserRole)
  const currentRole = room.currentUserRole || 'viewer';
  const canManageSettings = currentRole === 'admin' || currentRole === 'super_admin';
  const canManageLinks = currentRole === 'editor' || currentRole === 'admin' || currentRole === 'super_admin';

  return (
    <div className="min-h-full pb-12">
        <div className="border-b border-neutral-200 bg-white sticky top-0 z-10">
            <div className="mx-auto w-full max-w-7xl px-6 sm:px-12">
                <div className="flex h-16 items-center gap-4">
                    <button 
                        onClick={() => navigate('/fundraising/vdr')}
                        className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="h-6 w-px bg-neutral-200" />

                    <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-neutral-900">{room.name}</span>
                        <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full capitalize border border-neutral-200">
                             {currentRole.replace('_', ' ')}
                        </span>
                    </div>

                    <div className="ml-8 flex items-center gap-1">
                        <TabButton 
                            active={activeTab === 'general'} 
                            onClick={() => setActiveTab('general')}
                        >
                            General Settings
                        </TabButton>
                        <TabButton 
                            active={activeTab === 'slugs'} 
                            onClick={() => setActiveTab('slugs')}
                        >
                            Secondary Slugs
                        </TabButton>
                        <TabButton 
                            active={activeTab === 'tracking'} 
                            onClick={() => setActiveTab('tracking')}
                        >
                            Visitor Tracking
                        </TabButton>
                    </div>
                </div>
            </div>
        </div>

      <div className="w-full max-w-7xl mx-auto px-6 sm:px-12 space-y-8 mt-8">
        {/* General Settings Section */}
        {activeTab === 'general' && (
            <>
                <Card className={!canManageSettings ? "opacity-60 pointer-events-none" : ""}>
                   <CardHeader>
                     <CardTitle>General Settings {(!canManageSettings) && "(Admin Only)"}</CardTitle>
                   </CardHeader>
                   <CardContent className="p-6">
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-1.5">URL Slug</label>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-neutral-400 shrink-0">{baseUrl}/</span>
                                <Input 
                                    value={slug} 
                                    onChange={e => setSlug(e.target.value)} 
                                    placeholder="e.g. content-rewards"
                                    disabled={!canManageSettings}
                                    className="flex-1"
                                />
                            </div>
                            <p className="text-xs text-neutral-400 mt-1.5">The main entry point for this data room.</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1.5">Master Password</label>
                            <Input 
                                value={accessCode} 
                                onChange={e => setAccessCode(e.target.value)} 
                                placeholder="Secret code..."
                                disabled={!canManageSettings}
                            />
                            <p className="text-xs text-neutral-400 mt-1.5">Optional password for the main slug.</p>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium mb-1.5">Logo URL</label>
                            <Input 
                                value={logoUrl} 
                                onChange={e => setLogoUrl(e.target.value)} 
                                placeholder="https://..."
                                disabled={!canManageSettings}
                            />
                            <p className="text-xs text-neutral-400 mt-1.5">Public URL for the VDR logo (Supabase Storage).</p>
                        </div>
        
                        <div className="md:col-span-2 pt-4 border-t border-neutral-100">
                            <h3 className="text-sm font-medium mb-3">Security & Access Control</h3>
                            <div className="bg-neutral-50 p-4 rounded border border-neutral-100 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-medium mb-1.5">Access Mode</label>
                                        <Select 
                                            value={mainSecurityLevel} 
                                            onChange={e => setMainSecurityLevel(e.target.value)}
                                            disabled={!canManageSettings}
                                        >
                                            <option value="OPEN">Open (Public with Link)</option>
                                            <option value="PASSWORD">Password Protected</option>
                                            <option value="EMAIL_OTP">Email Verification (OTP)</option>
                                            <option value="REQUEST_ACCESS">Request Access Form</option>
                                        </Select>
                                    </div>
        
                                    {mainSecurityLevel === 'PASSWORD' && (
                                        <div>
                                            <label className="block text-xs font-medium mb-1.5">Required Password</label>
                                            <Input 
                                                value={mainPassword} 
                                                onChange={e => setMainPassword(e.target.value)} 
                                                placeholder="Secret..."
                                                disabled={!canManageSettings}
                                            />
                                        </div>
                                    )}
                                </div>
        
                                {mainSecurityLevel === 'EMAIL_OTP' && (
                                    <div>
                                        <label className="block text-xs font-medium mb-1.5">Allowed Domain Groups (Optional)</label>
                                        <div className="space-y-2 border rounded p-3 bg-white max-h-32 overflow-y-auto">
                                            {domainGroups.length === 0 && <div className="text-xs text-neutral-400">No domain groups found.</div>}
                                            {domainGroups.map((group: any) => (
                                                <label key={group.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-neutral-50 p-1 rounded">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={mainAllowedGroups.includes(group.id)}
                                                        onChange={e => {
                                                            if (!canManageSettings) return;
                                                            if (e.target.checked) setMainAllowedGroups([...mainAllowedGroups, group.id]);
                                                            else setMainAllowedGroups(mainAllowedGroups.filter(id => id !== group.id));
                                                        }}
                                                        className="rounded border-neutral-300"
                                                        disabled={!canManageSettings}
                                                    />
                                                    <span>{group.name}</span>
                                                </label>
                                            ))}
                                        </div>
                                        <p className="text-xs text-neutral-400 mt-1.5">
                                            If selected, only emails from these domains can verify. If none selected, any valid email can verify.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                     </div>
                     {canManageSettings && (
                        <div className="mt-6 flex justify-end">
                            <Button onClick={handleSaveSettings} text="Save General Settings" />
                        </div>
                     )}
                   </CardContent>
                </Card>
        
                {/* Page Settings */}
                <Card className={!canManageSettings ? "opacity-60 pointer-events-none" : ""}>
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle>Page Settings</CardTitle>
                                <p className="text-sm text-neutral-500">Manage navigation and content pages.</p>
                            </div>
                            {canManageSettings && (
                                <Button 
                                    variant="outline" 
                                    onClick={() => setIsCreatingPage(!isCreatingPage)} 
                                    text={isCreatingPage ? "Cancel" : "Add Page"} 
                                />
                            )}
                        </div>
                    </CardHeader>
                    <CardContent className="p-6">
                        {isCreatingPage && (
                            <div className="mb-6 p-4 bg-neutral-50 rounded border border-neutral-100">
                                <PageForm 
                                    roomId={roomId} 
                                    userRole={userRole} 
                                    userId={userId} 
                                    onSuccess={() => { setIsCreatingPage(false); loadPages(); }}
                                    onCancel={() => setIsCreatingPage(false)}
                                />
                            </div>
                        )}
        
                        <div className="space-y-4">
                            {pages.map((page) => (
                                <PageRow 
                                    key={page.id} 
                                    page={page} 
                                    roomId={roomId}
                                    userRole={userRole}
                                    userId={userId}
                                    onUpdate={loadPages}
                                    onDelete={() => handleDeletePage(page.id)}
                                    canManage={canManageSettings}
                                />
                            ))}
                            {pages.length === 0 && !isCreatingPage && (
                                <div className="text-center text-neutral-400 py-8">No pages found. Add one to get started.</div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </>
        )}

        {/* Secondary Slugs Section */}
        {activeTab === 'slugs' && (
            <Card>
                <CardHeader>
                    <CardTitle>Secondary Slugs</CardTitle>
                    <p className="text-sm text-neutral-500">Create alternative access links with specific security controls.</p>
                </CardHeader>
                <CardContent className="p-6">
                    {/* Create Form */}
                    {canManageLinks && (
                        <form onSubmit={handleCreateLink} className="bg-neutral-50 p-4 rounded-lg border border-neutral-100 mb-6">
                            <h3 className="text-sm font-medium mb-3">Create New Slug</h3>
                            <div className="flex flex-col md:flex-row gap-4 items-end">
                                <div className="flex-1 w-full">
                                    <label className="block text-xs font-medium mb-1">Label</label>
                                    <Input 
                                        value={linkLabel} 
                                        onChange={e => setLinkLabel(e.target.value)} 
                                        placeholder="e.g. Investors Q1"
                                        required 
                                    />
                                </div>
                                <div className="md:w-1/3 w-full">
                                    <label className="block text-xs font-medium mb-1">Custom Code (Optional)</label>
                                    <Input 
                                        value={customCode} 
                                        onChange={e => setCustomCode(e.target.value)} 
                                        placeholder="Leave empty to auto-gen"
                                    />
                                </div>
                                <Button type="submit" text="Generate Slug" />
                            </div>
                        </form>
                    )}
    
                    {/* List */}
                    <div className="border rounded-lg divide-y divide-neutral-100">
                        {links.length === 0 ? (
                            <div className="p-8 text-center text-neutral-400 text-sm">No secondary slugs created.</div>
                        ) : (
                            links.map(link => (
                                <SlugRow 
                                    key={link.id} 
                                    link={link} 
                                    baseUrl={baseUrl} 
                                    onDelete={() => handleDeleteLink(link.id)}
                                    domainGroups={domainGroups}
                                    userRole={userRole}
                                    userId={userId}
                                    roomId={roomId}
                                    onUpdate={loadLinks}
                                    canManage={canManageLinks}
                                />
                            ))
                        )}
                    </div>
                </CardContent>
            </Card>
        )}

        {/* Tracking Section */}
        {activeTab === 'tracking' && roomId && (
             <VDRTrackingTable roomId={roomId} />
        )}

      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean, onClick: () => void, children: React.ReactNode }) {
    return (
        <button
            onClick={onClick}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                active 
                    ? 'bg-neutral-100 text-neutral-900' 
                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
            }`}
        >
            {children}
        </button>
    );
}

// Sub-component for Page Form
function PageForm({ page, roomId, userRole, userId, onSuccess, onCancel }: any) {
    const [title, setTitle] = useState(page?.title || '');
    const [slug, setSlug] = useState(page?.slug || '');
    const [content, setContent] = useState(page?.content || '');
    const [orderIndex, setOrderIndex] = useState(page?.order_index || 0);
    
    // Security State
    const [securityLevel, setSecurityLevel] = useState(page?.security_level || 'OPEN');
    const [pagePassword, setPagePassword] = useState(page?.security_config?.password || '');
    const [allowedGroups, setAllowedGroups] = useState<string[]>(page?.security_config?.allowed_domain_group_ids || []);

    // Load domain groups for selection
    const [domainGroups, setDomainGroups] = useState<any[]>([]);
    
    useEffect(() => {
        // Fetch domain groups if we need them (simplified: reusing the ones from parent if possible, but fetching here to be safe/clean)
        fetch(`${import.meta.env.DEV ? "http://localhost:3001/api" : "/api"}/domain-groups`, { headers: { 'x-user-role': userRole } })
            .then(res => res.json())
            .then(setDomainGroups)
            .catch(console.error);
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const url = page 
                ? `${API_URL}/vdr/${roomId}/pages/${page.id}`
                : `${API_URL}/vdr/${roomId}/pages`;
            
            const method = page ? 'PUT' : 'POST';
            
            const securityConfig = {
                password: securityLevel === 'PASSWORD' ? pagePassword : null,
                allowed_domain_group_ids: securityLevel === 'EMAIL_OTP' ? allowedGroups : []
            };

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json', 'x-user-role': userRole, 'x-user-id': userId },
                body: JSON.stringify({ 
                    title, 
                    slug: slug || title.toLowerCase().replace(/\s+/g, '-'), 
                    content, 
                    orderIndex,
                    securityLevel,
                    securityConfig
                })
            });

            if (res.ok) {
                onSuccess();
            } else {
                alert('Failed to save page');
            }
        } catch (e) { alert('Error saving page'); }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium mb-1">Title</label>
                    <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Page Title" required />
                </div>
                <div>
                    <label className="block text-xs font-medium mb-1">Slug</label>
                    <Input value={slug} onChange={e => setSlug(e.target.value)} placeholder="page-slug" />
                </div>
            </div>
            
            {/* Security Settings */}
            <div className="bg-neutral-50/50 p-3 rounded border border-neutral-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium mb-1">Security Level</label>
                        <Select 
                            value={securityLevel} 
                            onChange={e => setSecurityLevel(e.target.value)}
                        >
                            <option value="OPEN">Open (Public)</option>
                            <option value="PASSWORD">Password Protected</option>
                            <option value="EMAIL_OTP">Email Verification (OTP)</option>
                        </Select>
                    </div>
                    
                    {securityLevel === 'PASSWORD' && (
                        <div>
                            <label className="block text-xs font-medium mb-1">Required Password</label>
                            <Input 
                                value={pagePassword} 
                                onChange={e => setPagePassword(e.target.value)} 
                                placeholder="Secret..."
                            />
                        </div>
                    )}
                </div>

                {securityLevel === 'EMAIL_OTP' && (
                    <div className="mt-3">
                        <label className="block text-xs font-medium mb-1">Allowed Domain Groups</label>
                        <div className="space-y-2 border rounded p-3 bg-white max-h-32 overflow-y-auto">
                            {domainGroups.length === 0 && <div className="text-xs text-neutral-400">No domain groups found.</div>}
                            {domainGroups.map((group: any) => (
                                <label key={group.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-neutral-50 p-1 rounded">
                                    <input 
                                        type="checkbox" 
                                        checked={allowedGroups.includes(group.id)}
                                        onChange={e => {
                                            if (e.target.checked) setAllowedGroups([...allowedGroups, group.id]);
                                            else setAllowedGroups(allowedGroups.filter(id => id !== group.id));
                                        }}
                                        className="rounded border-neutral-300"
                                    />
                                    <span>{group.name}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div>
                <label className="block text-xs font-medium mb-1">Content (Markdown/HTML)</label>
                <textarea 
                    className="w-full min-h-[200px] p-3 border border-neutral-200 rounded-md text-sm font-mono focus:outline-none focus:border-neutral-400"
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    placeholder="Enter Markdown or HTML content here..."
                />
            </div>
            <div className="flex items-center gap-4">
                <div className="w-24">
                    <label className="block text-xs font-medium mb-1">Order</label>
                    <Input type="number" value={orderIndex} onChange={e => setOrderIndex(parseInt(e.target.value))} />
                </div>
                <div className="flex-1 flex justify-end gap-2 pt-5">
                    <Button type="button" variant="outline" onClick={onCancel} text="Cancel" />
                    <Button type="submit" text={page ? "Update Page" : "Create Page"} />
                </div>
            </div>
        </form>
    );
}

// Sub-component for Page Row
function PageRow({ page, roomId, userRole, userId, onUpdate, onDelete, canManage }: any) {
    const [isEditing, setIsEditing] = useState(false);

    if (isEditing) {
        return (
            <div className="bg-neutral-50 p-4 rounded border border-neutral-200">
                <PageForm 
                    page={page}
                    roomId={roomId}
                    userRole={userRole}
                    userId={userId}
                    onSuccess={() => { setIsEditing(false); onUpdate(); }}
                    onCancel={() => setIsEditing(false)}
                />
            </div>
        );
    }

    return (
        <div className="flex items-center justify-between p-4 bg-white border border-neutral-100 rounded hover:border-neutral-200 transition-colors">
            <div>
                <div className="font-medium text-sm">{page.title}</div>
                <div className="text-xs text-neutral-400 font-mono">/{page.slug}</div>
            </div>
            <div className="flex items-center gap-4">
                <div className="text-xs text-neutral-400">Order: {page.order_index}</div>
                {canManage && (
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={() => setIsEditing(true)} text="Edit" />
                        <Button 
                            variant="danger-outline" 
                            className="bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300" 
                            onClick={onDelete} 
                            text="Delete" 
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

// Sub-component for Slug Row to handle individual settings state
function SlugRow({ link, baseUrl, onDelete, domainGroups, userRole, userId, roomId, onUpdate, canManage }: any) {
    const [showSettings, setShowSettings] = useState(false);
    
    // Local state for edits
    const [securityLevel, setSecurityLevel] = useState(link.security_level || 'open');
    const [linkPassword, setLinkPassword] = useState(link.link_password || '');
    const [allowedGroups, setAllowedGroups] = useState<string[]>(link.allowed_domain_group_ids || []);

    const handleSave = async () => {
        try {
            const res = await fetch(`${import.meta.env.DEV ? "http://localhost:3001/api" : "/api"}/vdr/${roomId}/links/${link.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'x-user-role': userRole, 'x-user-id': userId },
                body: JSON.stringify({ 
                    securityLevel, 
                    linkPassword: securityLevel === 'password' ? linkPassword : null,
                    allowedDomainGroupIds: securityLevel === 'email' ? allowedGroups : []
                })
            });
            if (res.ok) {
                alert('Slug settings saved');
                setShowSettings(false);
                onUpdate();
            } else {
                alert('Failed to update slug');
            }
        } catch (e) { alert('Error updating slug'); }
    };

    const fullUrl = `http://${baseUrl}/${link.code}`;

    return (
        <div className="bg-white">
            <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <div className="font-medium text-sm text-neutral-900">{link.label}</div>
                    <div className="text-xs text-neutral-500 font-mono mt-1 flex items-center gap-2">
                        {baseUrl}/{link.code}
                        <span 
                            className="cursor-pointer text-blue-600 hover:text-blue-700"
                            onClick={() => {
                                navigator.clipboard.writeText(fullUrl);
                                alert('Copied!');
                            }}
                        >
                            (Copy)
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-1 rounded-full border ${
                        link.security_level === 'open' ? 'bg-green-50 text-green-700 border-green-200' :
                        link.security_level === 'password' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                        'bg-blue-50 text-blue-700 border-blue-200'
                    }`}>
                        {link.security_level === 'email' ? 'Email Protected' : 
                         link.security_level === 'password' ? 'Password Protected' : 'Open Access'}
                    </span>
                    
                    {canManage && (
                        <>
                            <Button variant="outline" onClick={() => setShowSettings(!showSettings)} text={showSettings ? 'Close Settings' : 'Settings'} />
                            <Button 
                                variant="danger-outline" 
                                className="bg-red-50 border-red-200 text-red-600 hover:bg-red-100 hover:border-red-300" 
                                onClick={onDelete}
                                text="Delete"
                            />
                        </>
                    )}
                </div>
            </div>
            
            {showSettings && canManage && (
                <div className="p-4 bg-neutral-50 border-t border-neutral-100 animate-in slide-in-from-top-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Access Control</h4>
                    
                    <div className="space-y-4 max-w-lg">
                        <div>
                            <label className="block text-sm font-medium mb-1.5">Security Level</label>
                            <Select 
                                value={securityLevel} 
                                onChange={e => setSecurityLevel(e.target.value)}
                            >
                                <option value="open">Open (Public with Link)</option>
                                <option value="password">Password Protected</option>
                                <option value="email">Email Verification (OTP)</option>
                            </Select>
                        </div>

                        {securityLevel === 'password' && (
                            <div>
                                <label className="block text-sm font-medium mb-1.5">Required Password</label>
                                <Input 
                                    type="text" 
                                    value={linkPassword} 
                                    onChange={e => setLinkPassword(e.target.value)} 
                                    placeholder="Enter password..."
                                />
                            </div>
                        )}

                        {securityLevel === 'email' && (
                            <div>
                                <label className="block text-sm font-medium mb-1.5">Allowed Domain Groups (Optional)</label>
                                <div className="space-y-2 border rounded p-3 bg-white max-h-40 overflow-y-auto">
                                    {domainGroups.length === 0 && <div className="text-xs text-neutral-400">No domain groups found. Create one in Admin &gt; Domain Groups.</div>}
                                    {domainGroups.map((group: any) => (
                                        <label key={group.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-neutral-50 p-1 rounded">
                                            <input 
                                                type="checkbox" 
                                                checked={allowedGroups.includes(group.id)}
                                                onChange={e => {
                                                    if (e.target.checked) setAllowedGroups([...allowedGroups, group.id]);
                                                    else setAllowedGroups(allowedGroups.filter(id => id !== group.id));
                                                }}
                                                className="rounded border-neutral-300"
                                            />
                                            <span>{group.name}</span>
                                            <span className="text-xs text-neutral-400">({group.domains?.length || 0} domains)</span>
                                        </label>
                                    ))}
                                </div>
                                <p className="text-xs text-neutral-400 mt-1.5">
                                    If selected, only emails from these domains can verify. If none selected, any valid email can verify.
                                </p>
                            </div>
                        )}

                        <div className="pt-2 flex justify-end">
                            <Button onClick={handleSave} text="Save Access Rules" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
